import { useEffect, useState } from 'react';
import { errorCodeOf } from '../../services/api-error';
import {
  categoryStandingsService,
  type CategoryStandings,
  type StandingsCategory,
} from '../../services/category-standings';

/** One class's standings, as far as they have come in. */
export type ClassResults =
  | { state: 'loading' }
  | { state: 'ready'; standings: CategoryStandings }
  | { state: 'not_closed' }
  | { state: 'failed' };

/**
 * Every class's standings at a stage, all asked for at once: the classes the
 * stage ran, and each one's table as it lands, keyed by class id. A class
 * whose turns are not all closed yet answers `not_closed` rather than an order
 * that may still change. No class at all when the list cannot be read.
 *
 * Shared by the results on the stage's page and their printable sheet.
 */
export function useCompetitionResults(competitionSlug: string | undefined) {
  const [categories, setCategories] = useState<StandingsCategory[]>([]);
  const [results, setResults] = useState<Record<string, ClassResults>>({});
  const [listed, setListed] = useState(false);

  useEffect(() => {
    if (!competitionSlug) return;
    let cancelled = false;
    const settle = (id: string, result: ClassResults) => {
      if (!cancelled) setResults((all) => ({ ...all, [id]: result }));
    };

    categoryStandingsService
      .list(competitionSlug)
      .then((answer) => {
        if (cancelled) return;
        setCategories(answer.categories);
        answer.categories.forEach(({ id }) => {
          categoryStandingsService
            .get(competitionSlug, id)
            .then((standings) => settle(id, { state: 'ready', standings }))
            .catch((error) =>
              settle(id, { state: errorCodeOf(error) === 'turns_not_closed' ? 'not_closed' : 'failed' })
            );
        });
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setListed(true));

    return () => {
      cancelled = true;
    };
  }, [competitionSlug]);

  const resultsOf = (id: string): ClassResults => results[id] ?? { state: 'loading' };
  const settled = listed && categories.every(({ id }) => resultsOf(id).state !== 'loading');

  return { categories, resultsOf, listed, settled };
}
